import type { GoogleAddressComponent } from "./googleAddressParts";
import { extractPlaceDisplayName, extractPlaceLatLng } from "./googleAddressParts";

export type AddressCandidateSource =
    | "search"
    | "poi-click"
    | "map-click"
    | "marker-drag"
    | "reverse-geocode";

export interface AddressCandidatePinCoordinates {
    latitude: number;
    longitude: number;
}

export interface AddressCandidate {
    source: AddressCandidateSource;
    displayName: string | null;
    formattedAddress: string | null;
    predictionDescription: string | null;
    addressComponents: GoogleAddressComponent[];
    latitude: number;
    longitude: number;
    placeId?: string | null;
    geocoderResultTypes?: string[];
    geocoderPlaceId?: string | null;
}

function shouldUsePinCoordinates(source: AddressCandidateSource): boolean {
    return source === "map-click" || source === "marker-drag" || source === "poi-click";
}

export function normalizeFromPlace(params: {
    source: AddressCandidateSource;
    place: google.maps.places.Place;
    pinCoords?: AddressCandidatePinCoordinates | null;
    formattedAddressOverride?: string | null;
    predictionDescription?: string | null;
    placeId?: string | null;
}): AddressCandidate | null {
    const placeCoords = extractPlaceLatLng(params.place.location);
    const usePin = params.pinCoords != null && shouldUsePinCoordinates(params.source);
    const latitude = usePin ? params.pinCoords!.latitude : placeCoords?.lat;
    const longitude = usePin ? params.pinCoords!.longitude : placeCoords?.lng;

    if (latitude == null || longitude == null) return null;

    return {
        source: params.source,
        displayName: extractPlaceDisplayName(params.place.displayName) || null,
        formattedAddress: params.formattedAddressOverride ?? params.place.formattedAddress ?? null,
        predictionDescription: params.predictionDescription ?? null,
        addressComponents: params.place.addressComponents ?? [],
        latitude,
        longitude,
        placeId: params.placeId ?? null,
    };
}

export function normalizeFromPlacePrediction(params: {
    place: google.maps.places.Place;
    placePrediction: google.maps.places.PlacePrediction;
}): AddressCandidate | null {
    const predictionText =
        params.placePrediction.text?.text ?? params.placePrediction.mainText?.text ?? null;

    return normalizeFromPlace({
        source: "search",
        place: params.place,
        formattedAddressOverride: params.place.formattedAddress ?? predictionText,
        predictionDescription: predictionText,
        placeId: params.placePrediction.placeId ?? null,
    });
}

export function normalizeFromGeocoderResult(params: {
    source: AddressCandidateSource;
    result: google.maps.GeocoderResult;
    pinCoords: AddressCandidatePinCoordinates;
}): AddressCandidate {
    return {
        source: params.source,
        displayName: null,
        formattedAddress: params.result.formatted_address ?? null,
        predictionDescription: null,
        addressComponents: params.result.address_components ?? [],
        latitude: params.pinCoords.latitude,
        longitude: params.pinCoords.longitude,
        geocoderResultTypes: params.result.types ?? [],
        geocoderPlaceId: params.result.place_id ?? null,
    };
}

export function normalizeFromManualInput(params: {
    source: AddressCandidateSource;
    displayName?: string | null;
    formattedAddress?: string | null;
    predictionDescription?: string | null;
    addressComponents: GoogleAddressComponent[];
    latitude: number;
    longitude: number;
}): AddressCandidate {
    return {
        source: params.source,
        displayName: params.displayName?.trim() || null,
        formattedAddress: params.formattedAddress ?? null,
        predictionDescription: params.predictionDescription ?? null,
        addressComponents: params.addressComponents,
        latitude: params.latitude,
        longitude: params.longitude,
    };
}
